import React, { useEffect, useRef, useMemo, useState, useCallback } from "react";
import { Card, Checkbox, Input, message, Modal, Spin, Splitter, Switch } from "antd";
import { useSearchParams } from "react-router-dom";
import { UserOutlined } from "@ant-design/icons";
import { ContainerPanel, ContainerModal, CompsList, CompsTree, CompsForm, CompsDescription } from "@ms/shared-components";
import type { GenericPanelTreeRef } from "@ms/shared-components/components/GenericPanelTree";

import {
  convertPermissionTreeToTreeNodeData,
  extractAssignedPermissionIds,
  updatePermissionTreeAssignedStatus,
} from "./utils/permissionTreeUtils";

import { useRoleList } from "./hooks/useRoleList";
import { useComponentHeight } from "./hooks/useComponentHeight";
import { usePermissionManagement } from "./hooks/usePermissionManagement";
import { useAdminPermissionManagement } from "./hooks/useAdminPermissionManagement";
import { useAdminList } from "./hooks/useAdminList";
import { useSelectionState } from "./hooks/useSelectionState";
import { useSelectionHandlers } from "./hooks/useSelectionHandlers";
import {
  cardStyles,
} from "./config/pageConfig";
import { buildAdminListItem, buildRoleListItem, buildRoleMemberCompsItem } from "./config/listConfig";
import { createRole, deleteRole, getRoleById, getRoles, updateRole } from "../roles/services/roleService";
import { transformFormDataToApiFormat } from "../roles/utils/roleUtils";
import { roleApi } from "../roles/utils/roleApi";
import type { CompsListItemData } from "@ms/shared-components/comps/CompsList";
import { roleModalConfig } from "../roles/config/modalFormConfig";

const RolePermissionManagement: React.FC = () => {
  const [searchParams] = useSearchParams();
  const roleIdFromUrl = searchParams.get("roleId");
  const permissionTreeRef = useRef<GenericPanelTreeRef>(null);
  const rolePermissionDataSourceRef = useRef<any[]>([]);
  const adminPermissionDataSourceRef = useRef<any[]>([]);
  const createRoleFormRef = useRef<any>(null);
  const editRoleFormRef = useRef<any>(null);
  /** 防止「角色成员」多次请求乱序返回时，旧响应覆盖新列表（表现为成员/admin 忽隐忽现） */
  const roleMembersFetchSeq = useRef(0);

  const [createRoleModalVisible, setCreateRoleModalVisible] = useState(false);
  const [editRoleModalVisible, setEditRoleModalVisible] = useState(false);
  const [rightRoleTab, setRightRoleTab] = useState<"permissions" | "roleInfo" | "members">("roleInfo");
  const [roleDetail, setRoleDetail] = useState<any>(null);
  const [roleDetailLoading, setRoleDetailLoading] = useState(false);
  const [roleMembersList, setRoleMembersList] = useState<CompsListItemData[]>([]);
  const [roleMembersLoading, setRoleMembersLoading] = useState(false);
  const [deleteRoleLoading, setDeleteRoleLoading] = useState(false);
  const [roleStatusUpdating, setRoleStatusUpdating] = useState(false);
  const [selectedMemberRowKeys, setSelectedMemberRowKeys] = useState<React.Key[]>([]);
  const [removeMembersLoading, setRemoveMembersLoading] = useState(false);
  const [addMembersModalVisible, setAddMembersModalVisible] = useState(false);
  const [addMemberSearch, setAddMemberSearch] = useState("");
  const [addMemberPickIds, setAddMemberPickIds] = useState<string[]>([]);
  const [addMembersSubmitting, setAddMembersSubmitting] = useState(false);

  // 选择状态管理
  const {
    selectedRole,
    selectedAdmin,
    hasUnsavedChanges,
    activeTab,
    setSelectedRole,
    setSelectedAdmin,
    setHasUnsavedChanges,
    setActiveTab,
  } = useSelectionState();

  // 组件高度
  const { componentHeight } = useComponentHeight();

  // 角色列表
  const { roleList, listLoading, fetchRoleList, updateRolePermissionCount } = useRoleList(roleIdFromUrl);

  // 权限管理
  const {
    rolePermissions,
    allPermissions,
    permissionLoading,
    permissionTableLoading,
    saveLoading,
    fetchRolePermissionsAndAll,
    handlePermissionChange,
    handleSavePermissions: onSavePermissions,
    handleRemovePermission: onRemovePermission,
    setRolePermissions,
  } = usePermissionManagement({ updateRolePermissionCount });

  // 管理员列表
  const { admins, updateAdminPermissionCount, loadAdmins } = useAdminList();

  // 管理员权限管理
  const {
    adminPermissions,
    rolePermissions: adminRolePermissions,
    extraPermissions: adminExtraPermissions,
    rolePermissionSources,
    permissionLoading: adminPermissionLoading,
    saveLoading: adminSaveLoading,
    fetchAdminPermissionsAndAll,
    handleSaveAdminPermissions,
    setAdminPermissions,
  } = useAdminPermissionManagement(updateAdminPermissionCount);

  // 更新数据源引用
  useEffect(() => {
    rolePermissionDataSourceRef.current = rolePermissions;
  }, [rolePermissions]);

  useEffect(() => {
    adminPermissionDataSourceRef.current = adminPermissions;
  }, [adminPermissions]);

  // 转换权限树数据为 TreeNodeData
  const roleTreeData = useMemo(() => {
    return convertPermissionTreeToTreeNodeData(rolePermissions, [], {});
  }, [rolePermissions]);

  const adminTreeData = useMemo(() => {
    return convertPermissionTreeToTreeNodeData(adminPermissions, adminRolePermissions, rolePermissionSources);
  }, [adminPermissions, adminRolePermissions, rolePermissionSources]);

  // 获取已分配的权限ID
  const roleCheckedKeys = useMemo(() => {
    return extractAssignedPermissionIds(rolePermissions);
  }, [rolePermissions]);

  const adminCheckedKeys = useMemo(() => {
    return extractAssignedPermissionIds(adminPermissions);
  }, [adminPermissions]);

  // 左侧列表数据：直接使用构建好的 ListItemData，包含 rightContent 与 roleData

  // 选择操作处理
  const {
    handleSelectRole,
    handleSelectAdmin,
    handleSavePermissions,
    handleRemovePermission,
    handlePermissionChangeCallback,
  } = useSelectionHandlers({
    selectedRole,
    selectedAdmin,
    setSelectedRole,
    setSelectedAdmin,
    setHasUnsavedChanges,
    fetchRolePermissionsAndAll,
    fetchAdminPermissionsAndAll,
    onSavePermissions,
    onRemovePermission,
    updateRolePermissionCount,
    handleSaveAdminPermissions,
    adminRolePermissions,
    permissionTreeRef,
    rolePermissionDataSourceRef,
    adminPermissionDataSourceRef,
  });

  // 处理权限选择变化（复选框）
  const handleRolePermissionCheck = (checkedKeys: React.Key[], info: any) => {
    const checkedKeyStrings = checkedKeys.map((key) => String(key));
    // 更新权限树中的 isAssigned 状态
    const updatedPermissions = updatePermissionTreeAssignedStatus(rolePermissions, checkedKeyStrings);
    setRolePermissions(updatedPermissions);
    // 标记有未保存的更改
    setHasUnsavedChanges(true);
    console.log("权限选择变化:", checkedKeyStrings.length, "个权限");
  };

  const handleAdminPermissionCheck = (checkedKeys: React.Key[], info: any) => {
    const checkedKeyStrings = checkedKeys.map((key) => String(key));
    // 更新权限树中的 isAssigned 状态
    const updatedPermissions = updatePermissionTreeAssignedStatus(adminPermissions, checkedKeyStrings);
    setAdminPermissions(updatedPermissions);
    // 标记有未保存的更改
    setHasUnsavedChanges(true);
    console.log("管理员权限选择变化:", checkedKeyStrings.length, "个权限");
  };

  // 处理节点选择（点击节点）
  const handleSelect = (selectedKeys: React.Key[], info: any) => {
    // 可以在这里处理节点点击选择，目前不需要特殊处理
    console.log("节点选择:", selectedKeys);
  };

  // 角色列表由 useRoleList 挂载时已 fetch；此处只拉管理员列表
  useEffect(() => {
    loadAdmins();
  }, [loadAdmins]);

  const loadRoleMembers = useCallback(async (roleId: string) => {
    const seq = ++roleMembersFetchSeq.current;
    setRoleMembersLoading(true);
    try {
      const resp = await roleApi.getRoleAdmins(roleId);
      if (seq !== roleMembersFetchSeq.current) return;
      if (resp.success && Array.isArray(resp.data)) {
        setRoleMembersList(
          (resp.data as Record<string, unknown>[]).map((a, i) => buildRoleMemberCompsItem(a, i)),
        );
      } else {
        setRoleMembersList([]);
        if (resp.success === false && resp.message) {
          message.warning(resp.message);
        }
      }
    } catch {
      if (seq !== roleMembersFetchSeq.current) return;
      setRoleMembersList([]);
      message.error("加载角色成员失败");
    } finally {
      if (seq === roleMembersFetchSeq.current) {
        setRoleMembersLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    // 切换选中角色时，默认回到「角色信息」tab，并加载角色详情
    if (!selectedRole?.id) {
      setRoleDetail(null);
      setRoleMembersList([]);
      return;
    }
    setRightRoleTab("roleInfo");
    setRoleDetailLoading(true);
    getRoleById(selectedRole.id)
      .then((detail) => setRoleDetail(detail))
      .catch(() => setRoleDetail(null))
      .finally(() => setRoleDetailLoading(false));
  }, [selectedRole?.id]);

  useEffect(() => {
    if (!selectedRole?.id || rightRoleTab !== "members") return;
    loadRoleMembers(selectedRole.id);
  }, [selectedRole?.id, rightRoleTab, loadRoleMembers]);

  useEffect(() => {
    setSelectedMemberRowKeys([]);
  }, [selectedRole?.id, rightRoleTab]);

  useEffect(() => {
    if (!addMembersModalVisible) return;
    setAddMemberSearch("");
    setAddMemberPickIds([]);
  }, [addMembersModalVisible]);

  const memberIdsInRole = useMemo(
    () => new Set(roleMembersList.map((i) => String(i.id))),
    [roleMembersList],
  );

  const adminsAvailableForRole = useMemo(
    () => admins.filter((a) => !memberIdsInRole.has(String(a.id))),
    [admins, memberIdsInRole],
  );

  const adminsForAddModalFiltered = useMemo(() => {
    const q = addMemberSearch.trim().toLowerCase();
    if (!q) return adminsAvailableForRole;
    return adminsAvailableForRole.filter(
      (a) =>
        (a.account && a.account.toLowerCase().includes(q)) ||
        (a.name && a.name.toLowerCase().includes(q)),
    );
  }, [adminsAvailableForRole, addMemberSearch]);

  const handleRemoveRoleMembers = useCallback(() => {
    if (!selectedRole?.id) return;
    const keys = selectedMemberRowKeys.filter((k) =>
      roleMembersList.some((row) => String(row.id) === String(k) && !row.memberRemoveDisabled),
    );
    if (keys.length === 0) {
      message.warning("请选择要移除的成员（内置 admin 账号不可移出）");
      return;
    }
    Modal.confirm({
      title: "移除成员",
      content: `确定将选中的 ${keys.length} 名管理员移出本角色？`,
      okText: "移除",
      okType: "danger",
      cancelText: "取消",
      onOk: async () => {
        setRemoveMembersLoading(true);
        try {
          await Promise.all(
            keys.map((adminId) =>
              roleApi.removeAdminFromRole(selectedRole.id, String(adminId)),
            ),
          );
          message.success("已移除成员");
          setSelectedMemberRowKeys([]);
          await loadRoleMembers(selectedRole.id);
        } catch (e: unknown) {
          // roleApi.request 已 message 提示
          throw e;
        } finally {
          setRemoveMembersLoading(false);
        }
      },
    });
  }, [selectedRole?.id, selectedMemberRowKeys, roleMembersList, loadRoleMembers]);

  const handleAddMembersConfirm = useCallback(async () => {
    if (!selectedRole?.id) return;
    if (addMemberPickIds.length === 0) {
      message.warning("请勾选要添加的管理员");
      return;
    }
    setAddMembersSubmitting(true);
    try {
      const resp = await roleApi.assignAdminsToRole(selectedRole.id, {
        adminIds: addMemberPickIds,
      });
      if (resp.success === false) {
        message.error(resp.message || "添加成员失败");
        return;
      }
      message.success("已添加成员");
      setAddMembersModalVisible(false);
      setAddMemberPickIds([]);
      await loadRoleMembers(selectedRole.id);
    } catch {
      // roleApi.request 已 message 提示
    } finally {
      setAddMembersSubmitting(false);
    }
  }, [selectedRole?.id, addMemberPickIds, loadRoleMembers]);

  // 角色列表加载后：优先 URL 中的 roleId；否则缺省选中第一项（需在「角色权限」Tab）
  useEffect(() => {
    if (activeTab !== "roles" || listLoading || roleList.length === 0) return;

    if (roleIdFromUrl) {
      const byUrl = roleList.find((r) => String(r.id) === String(roleIdFromUrl));
      if (byUrl) {
        if (String(selectedRole?.id ?? "") !== String(byUrl.id)) {
          handleSelectRole(byUrl);
        }
        return;
      }
    }

    const stillInList =
      selectedRole != null &&
      roleList.some((r) => String(r.id) === String(selectedRole.id));
    if (!stillInList) {
      handleSelectRole(roleList[0]);
    }
  }, [activeTab, listLoading, roleList, roleIdFromUrl, selectedRole, handleSelectRole]);

  const roleCodeUpper = (
    (roleDetail?.code ?? selectedRole?.code ?? "") as string
  )
    .toString()
    .toUpperCase();
  const isSystemBuiltinRole =
    roleCodeUpper === "SUPER-ADMINS" || roleCodeUpper === "SYSTEM_ADMIN";

  const handleRoleStatusChange = useCallback(
    async (checked: boolean) => {
      if (!selectedRole?.id || roleDetailLoading || !roleDetail) return;
      setRoleStatusUpdating(true);
      try {
        const resp = await updateRole(selectedRole.id, { isActive: checked });
        if (resp?.success === false) {
          message.error(resp?.message || "更新状态失败");
          return;
        }
        message.success(checked ? "已启用" : "已禁用");
        const detail = await getRoleById(selectedRole.id);
        setRoleDetail(detail);
        await fetchRoleList();
      } catch {
        // axios 拦截器已提示
      } finally {
        setRoleStatusUpdating(false);
      }
    },
    [selectedRole?.id, roleDetail, roleDetailLoading, fetchRoleList],
  );

  useEffect(() => {
    if (!editRoleModalVisible || !roleDetail) return;
    const timer = window.setTimeout(() => {
      editRoleFormRef.current?.setFieldsValue?.({
        name: roleDetail.name,
        code: roleDetail.code,
        description: roleDetail.description ?? "",
        status: roleDetail.is_active ? "active" : "inactive",
      });
    }, 0);
    return () => window.clearTimeout(timer);
  }, [editRoleModalVisible, roleDetail]);

  const handleDeleteSelectedRole = useCallback(() => {
    if (!selectedRole?.id) return;
    if (isSystemBuiltinRole) {
      message.warning("不能删除系统内置角色");
      return;
    }
    const displayName =
      selectedRole.name || (selectedRole as { leftContent?: string }).leftContent || selectedRole.id;
    Modal.confirm({
      title: "删除角色",
      content: `确定删除角色「${displayName}」？此操作不可恢复。`,
      okText: "删除",
      okType: "danger",
      cancelText: "取消",
      onOk: async () => {
        setDeleteRoleLoading(true);
        try {
          await deleteRole(selectedRole.id);
          message.success("已删除角色");
          setSelectedRole(null);
          setRoleDetail(null);
          setHasUnsavedChanges(false);
          await fetchRoleList();
        } catch (e: unknown) {
          // axios 拦截器已提示错误文案，此处仅阻止 Modal 关闭
          throw e;
        } finally {
          setDeleteRoleLoading(false);
        }
      },
    });
  }, [
    selectedRole,
    isSystemBuiltinRole,
    setSelectedRole,
    setHasUnsavedChanges,
    fetchRoleList,
  ]);

  // 角色树形数据（目前未使用，保留以备将来需要）
  // const roleTreeData = buildRoleTreeData(roleList as unknown as RoleNode[]);

  return (
    <div style={{ height: "100vh", display: "flex", flexDirection: "column", paddingLeft: 12, paddingRight: 12 }}>
      <Splitter style={{ flex: 1, minHeight: 0 }}>
        <Splitter.Panel defaultSize={480} min={360} max={720} style={{ minWidth: 0, paddingRight: 12 }}>
          <ContainerPanel
            title="授权对象"
            tabs={[
              { key: "roles", label: "角色权限" },
              { key: "admins", label: "管理员权限" },
            ]}
            activeTabKey={activeTab}
            onTabChange={setActiveTab}
            buttons={[
              {
                key: "refresh",
                text: "刷新",
                type: "default",
                onClick: (): void => {
                  if (activeTab === "roles") {
                    fetchRoleList();
                  } else {
                    loadAdmins();
                  }
                },
                group: "secondary",
              },
              ...(activeTab === "roles"
                ? [
                    {
                      key: "addRole",
                      text: "新增",
                      type: "primary" as const,
                      onClick: () => setCreateRoleModalVisible(true),
                      group: "primary" as const,
                    },
                  ]
                : []),
            ]}
            cardProps={{ style: { ...cardStyles, width: "100%", minWidth: 0 } }}
          >
            {activeTab === "roles" ? (
              <CompsList
                listData={roleList}
                loading={listLoading}
                selectedId={selectedRole?.id}
                onSelect={(item): void => handleSelectRole(item as any)}
                showSearch={true}
              />
            ) : (
              <CompsList
                listData={admins.map(buildAdminListItem)}
                loading={false}
                selectedId={selectedAdmin?.id}
                onSelect={(item): void => handleSelectAdmin((item as any).adminData)}
                showSearch={true}
              />
            )}
          </ContainerPanel>
        </Splitter.Panel>

        <Splitter.Panel style={{ minWidth: 0, paddingLeft: 12 }}>
          {selectedRole ? (
            <ContainerPanel
              title={`角色 - ${selectedRole.name || selectedRole.leftContent || "未命名"}`}
              tabs={[
                { key: "roleInfo", label: "角色信息" },
                { key: "permissions", label: "角色权限" },
                { key: "members", label: "角色成员" },
              ]}
              activeTabKey={rightRoleTab}
              onTabChange={(k) => setRightRoleTab(k as "permissions" | "roleInfo" | "members")}
              buttons={[
                ...(rightRoleTab === "roleInfo"
                  ? [
                      {
                        key: "deleteRole",
                        text: "删除角色",
                        type: "danger" as const,
                        group: "secondary" as const,
                        loading: deleteRoleLoading,
                        disabled: isSystemBuiltinRole,
                        title: isSystemBuiltinRole ? "系统内置角色不可删除" : undefined,
                        onClick: handleDeleteSelectedRole,
                      },
                    ]
                  : []),
                ...(rightRoleTab === "members"
                  ? [
                      {
                        key: "removeRoleMembers",
                        text: "删除成员",
                        type: "danger" as const,
                        group: "secondary" as const,
                        disabled: selectedMemberRowKeys.length === 0,
                        loading: removeMembersLoading,
                        onClick: handleRemoveRoleMembers,
                      },
                      {
                        key: "addRoleMembers",
                        text: "添加成员",
                        type: "primary" as const,
                        group: "primary" as const,
                        onClick: () => setAddMembersModalVisible(true),
                      },
                    ]
                  : []),
                ...(rightRoleTab === "permissions"
                  ? [
                      {
                        key: "save",
                        text: "保存",
                        type: "primary" as const,
                        onClick: handleSavePermissions,
                        group: "primary" as const,
                      },
                    ]
                  : []),
                ...(rightRoleTab === "roleInfo"
                  ? [
                      {
                        key: "editRole",
                        text: "编辑",
                        type: "primary" as const,
                        group: "primary" as const,
                        disabled: roleDetailLoading || !roleDetail,
                        title: roleDetailLoading ? "加载中…" : !roleDetail ? "暂无角色详情" : undefined,
                        onClick: () => setEditRoleModalVisible(true),
                      },
                    ]
                  : []),
              ]}
              height="100%"
            >
              {rightRoleTab === "permissions" ? (
                <CompsTree
                  treeData={roleTreeData as any}
                  loading={permissionLoading}
                  checkable={true}
                  checkedKeys={roleCheckedKeys}
                  onCheck={handleRolePermissionCheck}
                  onSelect={handleSelect}
                  showSearch={true}
                  defaultExpandAll={false}
                  height="100%"
                />
              ) : rightRoleTab === "members" ? (
                <CompsList
                  listData={roleMembersList}
                  loading={roleMembersLoading}
                  showSearch={true}
                  searchPlaceholder="搜索管理员账号或姓名..."
                  height="100%"
                  rowSelection={{
                    selectedRowKeys: selectedMemberRowKeys,
                    onChange: setSelectedMemberRowKeys,
                    getCheckboxProps: (item) => ({
                      disabled: !!item.memberRemoveDisabled,
                    }),
                  }}
                />
              ) : (
                <div style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
                  <Spin spinning={roleDetailLoading}>
                    <CompsDescription
                      columns={2}
                      bordered
                      data={[
                        { key: "name", label: "角色名称", value: roleDetail?.name || selectedRole.name || "-" },
                        { key: "code", label: "角色编码", value: roleDetail?.code || selectedRole.code || "-" },
                        { key: "description", label: "描述", value: roleDetail?.description || "-" },
                        {
                          key: "status",
                          label: "状态",
                          value: "switch",
                          render: () => (
                            <Switch
                              checkedChildren="启用"
                              unCheckedChildren="禁用"
                              checked={!!roleDetail?.is_active}
                              loading={roleStatusUpdating}
                              disabled={roleDetailLoading || !roleDetail}
                              onChange={handleRoleStatusChange}
                            />
                          ),
                        },
                        { key: "created_at", label: "创建时间", value: roleDetail?.created_at || "-" },
                        { key: "updated_at", label: "更新时间", value: roleDetail?.updated_at || "-" },
                      ]}
                    />
                  </Spin>
                </div>
              )}
            </ContainerPanel>
          ) : selectedAdmin ? (
            <ContainerPanel
              title={`管理员权限 - ${selectedAdmin.name}`}
              buttons={[
                {
                  key: "save",
                  text: "保存",
                  type: "primary",
                  onClick: handleSavePermissions,
                  group: "primary",
                },
              ]}
              height="100%"
            >
              <CompsTree
                treeData={adminTreeData as any}
                loading={adminPermissionLoading}
                checkable={true}
                checkedKeys={adminCheckedKeys}
                onCheck={handleAdminPermissionCheck}
                onSelect={handleSelect}
                showSearch={true}
                defaultExpandAll={false}
                height="100%"
              />
            </ContainerPanel>
          ) : (
            <Card style={cardStyles}>
              <div style={{ textAlign: "center", padding: "50px 0" }}>
                <UserOutlined
                  style={{
                    fontSize: 48,
                    color: "#d9d9d9",
                    marginBottom: 16,
                  }}
                />
                <p>请从左侧选择角色或管理员</p>
              </div>
            </Card>
          )}
        </Splitter.Panel>
      </Splitter>

      <Modal
        title="添加成员"
        open={addMembersModalVisible}
        onCancel={() => setAddMembersModalVisible(false)}
        onOk={() => void handleAddMembersConfirm()}
        confirmLoading={addMembersSubmitting}
        okText="确定"
        cancelText="取消"
        width={520}
        destroyOnClose
      >
        <Input.Search
          allowClear
          placeholder="搜索账号或姓名"
          value={addMemberSearch}
          onChange={(e) => setAddMemberSearch(e.target.value)}
          style={{ marginBottom: 12 }}
        />
        <div style={{ maxHeight: 360, overflow: "auto" }}>
          {adminsForAddModalFiltered.length === 0 ? (
            <div style={{ color: "#999", padding: 16, textAlign: "center" }}>
              {adminsAvailableForRole.length === 0
                ? "暂无可添加的管理员（可能已全部在本角色中）"
                : "无匹配结果"}
            </div>
          ) : (
            adminsForAddModalFiltered.map((admin) => {
              const id = String(admin.id);
              return (
                <div key={id} style={{ padding: "6px 0" }}>
                  <Checkbox
                    checked={addMemberPickIds.includes(id)}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setAddMemberPickIds((prev) =>
                        checked ? [...prev, id] : prev.filter((x) => x !== id),
                      );
                    }}
                  >
                    {admin.name} ({admin.account})
                  </Checkbox>
                </div>
              );
            })
          )}
        </div>
      </Modal>

      <ContainerModal
        title="编辑角色"
        visible={editRoleModalVisible}
        width={roleModalConfig.modalProps.width}
        height={520}
        onCancel={() => setEditRoleModalVisible(false)}
        footerRightButtons={[
          {
            key: "cancel",
            text: roleModalConfig.modalProps.cancelText,
            type: "default",
            onClick: () => setEditRoleModalVisible(false),
          },
          {
            key: "ok",
            text: roleModalConfig.modalProps.okText,
            type: "primary",
            onClick: () => editRoleFormRef.current?.submit?.(),
          },
        ]}
        modalProps={{ destroyOnClose: roleModalConfig.modalProps.destroyOnClose }}
      >
        <CompsForm
          formItems={roleModalConfig.editFormConfig()}
          formRef={(form) => {
            editRoleFormRef.current = form;
          }}
          formProps={{
            onFinish: async (values: any) => {
              if (!selectedRole?.id) return false;
              try {
                const payload = transformFormDataToApiFormat({
                  ...values,
                  code: typeof values?.code === "string" ? values.code.toUpperCase() : values?.code,
                });
                const resp = await updateRole(selectedRole.id, payload);
                if (resp?.success === false) {
                  message.error(resp?.message || "更新角色失败");
                  return false;
                }
                message.success("更新角色成功");
                setEditRoleModalVisible(false);
                const detail = await getRoleById(selectedRole.id);
                setRoleDetail(detail);
                await fetchRoleList();
                if (detail) {
                  setSelectedRole((prev) =>
                    prev && prev.id === selectedRole.id
                      ? { ...prev, name: detail.name, code: detail.code }
                      : prev,
                  );
                }
                return true;
              } catch (e: any) {
                const msg =
                  e?.response?.data?.message ||
                  e?.response?.data?.error ||
                  e?.message ||
                  "更新角色失败";
                message.error(msg);
                return false;
              }
            },
          }}
          layout={roleModalConfig.modalProps.layout}
          labelCol={{ span: 6 }}
          wrapperCol={{ span: 16 }}
        />
      </ContainerModal>

      {/* 新增角色弹窗（从角色管理页复用表单配置与接口） */}
      <ContainerModal
        title="创建角色"
        visible={createRoleModalVisible}
        width={roleModalConfig.modalProps.width}
        height={500}
        onCancel={() => setCreateRoleModalVisible(false)}
        footerRightButtons={[
          {
            key: "cancel",
            text: roleModalConfig.modalProps.cancelText,
            type: "default",
            onClick: () => setCreateRoleModalVisible(false),
          },
          {
            key: "ok",
            text: roleModalConfig.modalProps.okText,
            type: "primary",
            onClick: () => createRoleFormRef.current?.submit(),
          },
        ]}
        modalProps={{ destroyOnClose: roleModalConfig.modalProps.destroyOnClose }}
      >
        <CompsForm
          formItems={roleModalConfig.createFormConfig()}
          formRef={(form) => (createRoleFormRef.current = form)}
          formProps={{
            onFinish: async (values: any) => {
              try {
                const payload = {
                  ...values,
                  code: typeof values?.code === "string" ? values.code.toUpperCase() : values?.code,
                };
                const resp = await createRole(payload);
                if (resp?.success === false) {
                  message.error(resp?.message || "创建角色失败");
                  return false;
                }
                message.success("创建角色成功");
                setCreateRoleModalVisible(false);
                await fetchRoleList();
                const newId = resp?.data?.id;
                if (newId) {
                  try {
                    const roles = await getRoles();
                    const created = roles.find((r) => String(r.id) === String(newId));
                    if (created) {
                      handleSelectRole(buildRoleListItem(created));
                    }
                  } catch {
                    /* 列表已刷新，选中新角色为体验优化 */
                  }
                }
                return true;
              } catch (e: any) {
                const msg =
                  e?.response?.data?.message ||
                  e?.response?.data?.error ||
                  e?.message ||
                  "创建角色失败";
                message.error(msg);
                return false;
              }
            },
          }}
          layout={roleModalConfig.modalProps.layout}
          labelCol={{ span: 6 }}
          wrapperCol={{ span: 16 }}
        />
      </ContainerModal>
    </div>
  );
};

export default RolePermissionManagement;
